import React, { useEffect, useMemo, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import type { AppTheme } from '../theme/appearanceTokens';
import { getAppearanceTokens } from '../theme/appearanceTokens';
import type { AppLanguage } from '../screens/SettingsScreen';
import { NotificationBell } from './NotificationBell';
import type { InAppNotification } from '../types';

export interface TopBarProfileProps {
  readonly userName: string;
  readonly avatarUrl: string | null;
  readonly language: AppLanguage;
  readonly theme: AppTheme;
  readonly onSettingsPress: () => void;
  readonly onLanguageChange: (language: AppLanguage) => Promise<void> | void;
  readonly onThemeChange: (theme: AppTheme) => Promise<void> | void;
  readonly notifications: InAppNotification[];
  readonly unreadNotificationsCount: number;
  readonly onNotificationPress: (notification: InAppNotification) => void;
  readonly onGoToNotificationsPress: () => void;
  readonly onEditNotificationsPress: () => void;
  readonly onMarkAllNotificationsAsRead: () => void;
  readonly onLogoutPress: () => void;
}

export const TopBarProfile: React.FC<TopBarProfileProps> = ({
  userName,
  avatarUrl,
  language,
  theme,
  onSettingsPress,
  onLanguageChange,
  onThemeChange,
  notifications,
  unreadNotificationsCount,
  onNotificationPress,
  onGoToNotificationsPress,
  onEditNotificationsPress,
  onMarkAllNotificationsAsRead,
  onLogoutPress,
}) => {
  const palette = useMemo(() => getAppearanceTokens(theme).topBarProfile, [theme]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  const userInitial = useMemo(() => {
    const normalizedName = userName.trim();
    if (!normalizedName) {
      return 'U';
    }
    return normalizedName.charAt(0).toUpperCase();
  }, [userName]);

  const toggleMenu = () => {
    setIsNotificationsOpen(false);
    setIsMenuOpen(previousValue => !previousValue);
  };

  const handleMenuItemPress = (onPress: () => void) => {
    setIsMenuOpen(false);
    setIsNotificationsOpen(false);
    setIsLanguageMenuOpen(false);
    setIsThemeMenuOpen(false);
    onPress();
  };

  const latestNotifications = useMemo(() => notifications.slice(0, 3), [notifications]);
  const formatNotificationRelativeTime = (value: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.max(0, Math.floor(diffMs / (1000 * 60)));
    if (diffMinutes < 1) {
      return 'Just now';
    }

    if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  };

  // TEMP: demo scope - keep language/theme fixed for client presentation.
  // const selectedLanguageLabel = language === 'es' ? 'Español' : 'English';
  // const selectedThemeLabel = theme === 'dark' ? 'Dark' : 'Light';
  const selectedLanguageLabel = language === 'en' ? 'English' : 'English';
  const selectedThemeLabel = 'Light';

  return (
    <View style={styles.container}>
      <View style={styles.notificationWrapper}>
        <NotificationBell
          unreadCount={unreadNotificationsCount}
          iconColor={palette.menuItemValue}
          onPress={() => {
            setIsMenuOpen(false);
            setIsNotificationsOpen(previousValue => !previousValue);
          }}
        />
        {isNotificationsOpen && (
          <View
            style={[
              styles.notificationsDropdown,
              {
                backgroundColor: palette.dropdownBackground,
                borderColor: palette.dropdownBorder,
              },
            ]}
          >
            <View style={styles.notificationsHeader}>
              <Text style={[styles.notificationsTitle, { color: palette.menuItemText }]}>Notifications</Text>
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => void onMarkAllNotificationsAsRead()}
                accessibilityRole="button"
                accessibilityLabel="Mark all notifications as read"
              >
                <Text style={[styles.notificationsMarkAll, { color: palette.menuItemValue }]}>
                  Mark all as read
                </Text>
              </TouchableOpacity>
            </View>
            {latestNotifications.length > 0 ? (
              <ScrollView style={styles.notificationsList} nestedScrollEnabled>
                {latestNotifications.map(notification => (
                  <TouchableOpacity
                    key={notification.id}
                    style={styles.notificationItem}
                    activeOpacity={0.75}
                    accessibilityRole="button"
                    accessibilityLabel={`Open notification: ${notification.title}`}
                    onPress={() => {
                      setIsNotificationsOpen(false);
                      void onNotificationPress(notification);
                    }}
                  >
                    <View style={styles.notificationItemTitleRow}>
                      <Text style={[styles.notificationItemTitle, { color: palette.menuItemText }]} numberOfLines={1}>
                        {notification.title}
                      </Text>
                      {!notification.isRead && <View style={styles.notificationUnreadDot} />}
                    </View>
                    <Text style={[styles.notificationItemBody, { color: palette.menuItemValue }]} numberOfLines={1}>
                      {notification.body}
                    </Text>
                    <Text style={[styles.notificationItemDate, { color: palette.menuItemValue }]}>
                      {formatNotificationRelativeTime(notification.createdAt)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            ) : (
              <Text style={[styles.notificationsEmpty, { color: palette.menuItemValue }]}>
                You have no notifications.
              </Text>
            )}
            <View style={[styles.menuDivider, { backgroundColor: palette.menuDivider }]} />
            <TouchableOpacity
              style={styles.notificationsGoTo}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Edit notifications"
              onPress={() => {
                setIsNotificationsOpen(false);
                onEditNotificationsPress();
              }}
            >
              <Text style={[styles.notificationsGoToText, { color: palette.menuItemText }]}>
                Edit notifications
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.notificationsGoTo}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Go to notifications page"
              onPress={() => {
                setIsNotificationsOpen(false);
                onGoToNotificationsPress();
              }}
            >
              <Text style={[styles.notificationsGoToText, { color: palette.menuItemText }]}>
                Go to notifications
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      <TouchableOpacity style={styles.trigger} activeOpacity={0.8} onPress={toggleMenu}>
        {avatarUrl && !avatarLoadFailed ? (
          <Image
            source={{ uri: avatarUrl }}
            style={styles.avatarImage}
            resizeMode="cover"
            onError={() => setAvatarLoadFailed(true)}
          />
        ) : (
          <View style={[styles.avatarFallback, { backgroundColor: palette.avatarFallbackBackground }]}>
            <Text style={[styles.avatarFallbackText, { color: palette.avatarFallbackText }]}>{userInitial}</Text>
          </View>
        )}
        <Text style={[styles.userName, { color: palette.triggerText }]} numberOfLines={1}>
          {userName}
        </Text>
        <Text style={[styles.chevron, { color: palette.triggerChevron }]}>{isMenuOpen ? '▴' : '▾'}</Text>
      </TouchableOpacity>

      {isMenuOpen && (
        <View
          style={[
            styles.dropdown,
            {
              backgroundColor: palette.dropdownBackground,
              borderColor: palette.dropdownBorder,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.menuItem}
            activeOpacity={0.7}
            onPress={() => handleMenuItemPress(onSettingsPress)}
          >
            <Text style={[styles.menuItemText, { color: palette.menuItemText }]}>Settings</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.menuItem}
            activeOpacity={0.7}
            onPress={() => {
              setIsLanguageMenuOpen(previousValue => !previousValue);
              setIsThemeMenuOpen(false);
            }}
          >
            <View style={styles.menuItemRow}>
              <Text style={[styles.menuItemText, { color: palette.menuItemText }]}>Language</Text>
              <Text style={[styles.menuItemValue, { color: palette.menuItemValue }]}>{selectedLanguageLabel}</Text>
            </View>
          </TouchableOpacity>
          {isLanguageMenuOpen && (
            <View
              style={[
                styles.submenuContainer,
                {
                  backgroundColor: palette.submenuBackground,
                  borderColor: palette.submenuBorder,
                },
              ]}
            >
              <TouchableOpacity
                style={styles.submenuItem}
                activeOpacity={0.7}
                onPress={() => {
                  void onLanguageChange('en');
                  setIsLanguageMenuOpen(false);
                  setIsThemeMenuOpen(false);
                  setIsMenuOpen(false);
                }}
              >
                <Text style={[styles.submenuText, { color: palette.submenuText }]}>English</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submenuItem}
                activeOpacity={0.7}
                onPress={() => {
                  // TEMP: disabled for demo (single language available)
                  // void onLanguageChange('es');
                  setIsLanguageMenuOpen(false);
                  setIsThemeMenuOpen(false);
                  setIsMenuOpen(false);
                }}
              >
              </TouchableOpacity>
            </View>
          )}
          <TouchableOpacity
            style={styles.menuItem}
            activeOpacity={0.7}
            onPress={() => {
              setIsThemeMenuOpen(previousValue => !previousValue);
              setIsLanguageMenuOpen(false);
            }}
          >
            <View style={styles.menuItemRow}>
              <Text style={[styles.menuItemText, { color: palette.menuItemText }]}>Theme</Text>
              <Text style={[styles.menuItemValue, { color: palette.menuItemValue }]}>{selectedThemeLabel}</Text>
            </View>
          </TouchableOpacity>
          {isThemeMenuOpen && (
            <View
              style={[
                styles.submenuContainer,
                {
                  backgroundColor: palette.submenuBackground,
                  borderColor: palette.submenuBorder,
                },
              ]}
            >
              <TouchableOpacity
                style={styles.submenuItem}
                activeOpacity={0.7}
                onPress={() => {
                  void onThemeChange('light');
                  setIsThemeMenuOpen(false);
                  setIsLanguageMenuOpen(false);
                  setIsMenuOpen(false);
                }}
              >
                <Text style={[styles.submenuText, { color: palette.submenuText }]}>Light</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submenuItem}
                activeOpacity={0.7}
                onPress={() => {
                  // TEMP: disabled for demo (single theme available)
                  // void onThemeChange('dark');
                  setIsThemeMenuOpen(false);
                  setIsLanguageMenuOpen(false);
                  setIsMenuOpen(false);
                }}
              >
              </TouchableOpacity>
            </View>
          )}
          <View style={[styles.menuDivider, { backgroundColor: palette.menuDivider }]} />
          <TouchableOpacity
            style={styles.menuItem}
            activeOpacity={0.7}
            onPress={() => handleMenuItemPress(onLogoutPress)}
          >
            <Text style={[styles.menuItemText, styles.logoutText, { color: palette.logoutText }]}>Logout</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    zIndex: 2200,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
      default: {},
    }),
  },
  notificationWrapper: {
    position: 'relative',
    zIndex: 3200,
  },
  notificationsDropdown: {
    position: 'absolute',
    top: 42,
    right: 0,
    zIndex: 3200,
    width: 320,
    borderRadius: 12,
    borderWidth: 1,
    paddingTop: 10,
    paddingBottom: 8,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  notificationsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    marginBottom: 6,
  },
  notificationsTitle: {
    fontSize: 13,
    fontFamily: 'Montserrat_600SemiBold',
  },
  notificationsMarkAll: {
    fontSize: 12,
    fontFamily: 'Montserrat_600SemiBold',
  },
  notificationsList: {
    maxHeight: 220,
  },
  notificationItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  notificationItemTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
    gap: 8,
  },
  notificationItemTitle: {
    fontSize: 13,
    fontFamily: 'Montserrat_600SemiBold',
    flex: 1,
  },
  notificationItemBody: {
    fontSize: 12,
    fontFamily: 'Montserrat_500Medium',
  },
  notificationItemDate: {
    fontSize: 11,
    fontFamily: 'Montserrat_500Medium',
    marginTop: 4,
    opacity: 0.85,
  },
  notificationUnreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#2563EB',
  },
  notificationsEmpty: {
    fontSize: 12,
    fontFamily: 'Montserrat_500Medium',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  notificationsGoTo: {
    paddingHorizontal: 12,
    paddingTop: 2,
    paddingBottom: 4,
  },
  notificationsGoToText: {
    fontSize: 13,
    fontFamily: 'Montserrat_600SemiBold',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  avatarImage: {
    width: 34,
    height: 34,
    borderRadius: 17,
  },
  avatarFallback: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#4B5563',
    fontFamily: 'Montserrat_600SemiBold',
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    fontFamily: 'Montserrat_600SemiBold',
    maxWidth: 220,
  },
  chevron: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'Montserrat_600SemiBold',
  },
  dropdown: {
    position: 'absolute',
    top: 48,
    right: 0,
    zIndex: 3000,
    minWidth: 220,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
    ...Platform.select({
      web: {
        // @ts-ignore - valid web style
        overflow: 'visible',
      },
      default: {},
    }),
  },
  menuItem: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  menuItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  menuDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 6,
    marginHorizontal: 12,
  },
  menuItemText: {
    fontSize: 14,
    color: '#374151',
    fontFamily: 'Montserrat_500Medium',
  },
  menuItemValue: {
    fontSize: 13,
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  submenuContainer: {
    marginHorizontal: 14,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#F9FAFB',
  },
  submenuItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  submenuText: {
    fontSize: 13,
    color: '#374151',
    fontFamily: 'Montserrat_500Medium',
  },
  logoutText: {
    color: '#DC2626',
    fontFamily: 'Montserrat_600SemiBold',
  },
});
